import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import useSeo from "@/hooks/useSeo";
import { members, stats } from "@/mocks/members";

const principles = [
  {
    icon: "ri-file-list-3-line",
    title: "先事实，后观点",
    desc: "每个讨论区先给出可核查的时间线与关键看点，再开放观点楼层。没有信息量的转发不会被当作事实。",
  },
  {
    icon: "ri-scales-3-line",
    title: "允许结论相反",
    desc: "同一件事，我们欢迎不同答案。只要推理讲得通，立场不同不是问题，回避讨论才是。",
  },
  {
    icon: "ri-shield-star-line",
    title: "对事不对人",
    desc: "讨论聚焦现象与机制，不做人身攻击、不扩散隐私、不给未经证实的指控加冕。",
  },
  {
    icon: "ri-advertisement-line",
    title: "不做商业推广",
    desc: "站内不承接广告投放，讨论区严禁导流、带货与刷屏内容，让每一层都干净可读。",
  },
];

const timeline = [
  { year: "起步", title: "从一条深夜热评开始", desc: "最初只是几个人在评论区互相补充信息，后来发现「讨论本身」比「结论」更有意思。" },
  { year: "成型", title: "为每个话题开设讨论区", desc: "把零散的讨论收拢到话题下，配上时间线和关键看点，讨论终于有了结构。" },
  { year: "现在", title: "770+ 个话题，31.6 万条观点", desc: "深夜依旧是最热闹的时段，热榜每天都在换，但「认真聊天」这件事没变过。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 深夜吃瓜 · 一个认真聊天的话题社区",
    description:
      "了解深夜吃瓜的品牌定位、内容原则与发展理念。我们为每个热门话题开设专属讨论区，鼓励有信息量的观点表达，拒绝人身攻击与商业推广。",
    keywords: "关于深夜吃瓜,品牌介绍,内容原则,话题社区,讨论区,观点互动",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于深夜吃瓜",
        description: "深夜吃瓜的品牌定位、内容原则与发展理念。",
        inLanguage: "zh-CN",
        url: "https://ag11k.cn/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ag11k.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://ag11k.cn/about" },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="relative overflow-hidden border-b border-background-200/60">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20warm%20amber%20and%20coral%20light%20trails%20on%20a%20deep%20dark%20background%2C%20soft%20bokeh%20particles%2C%20cinematic%20moody%20atmosphere%2C%20minimal%20digital%20art%2C%20high%20detail&width=1600&height=700&seq=yc-about-01&orientation=landscape"
          alt="温暖的琥珀色光线在深色背景中流转，象征深夜吃瓜社区氛围"
          title="关于深夜吃瓜"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/85 via-background-950/80 to-background-950/95" />
        <div className="relative z-10 mx-auto max-w-content px-4 py-16 md:px-6 md:py-20">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="heading-serif mt-6 max-w-3xl text-3xl font-black leading-snug text-foreground-950 md:text-5xl">
            一个认真聊天的地方，
            <br />
            <span className="text-accent-500">而不是又一个吵架现场</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground-300">
            深夜吃瓜是一个专注热话题讨论的社区。我们把正在发酵的新闻做成一个个完整的讨论区：有事实梳理，有观点楼层，也有你最想看的评论区。你可以全程潜水，也可以随时加入。
          </p>
        </div>
      </section>

      <section className="border-b border-background-200/60 bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid max-w-content grid-cols-2 gap-4 px-4 md:grid-cols-4 md:gap-5 md:px-6">
          {stats.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 50}>
              <div className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-500">
                  <i className={`${stat.icon} text-lg`} />
                </span>
                <p className="brand-font mt-3 text-2xl text-foreground-950 md:text-3xl">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-b border-background-200/60 bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <span className="flex items-center gap-2 text-xs tracking-[0.3em] text-secondary-500">
                <i className="ri-flag-2-line" />
                OUR POSITION
              </span>
              <h2 className="heading-serif mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
                网站定位
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-8" delay={60}>
              <p className="text-base leading-[1.9] text-foreground-600">
                我们不做资讯搬运，也不做只看热闹的围观席。深夜吃瓜的核心，是给每个热话题一个结构化的讨论空间：它既解释了「发生了什么」，也保留了「你怎么看」。
                在这里，热点是入口，观点才是主角。我们相信真正有价值的讨论，往往发生在情绪退潮之后，而这恰好是深夜最适合做的事。
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <h3 className="heading-serif text-base font-bold text-foreground-950">对读者</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                    用最短时间看懂一件事的全貌，再决定要不要参与。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <h3 className="heading-serif text-base font-bold text-foreground-950">对讨论</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                    让有理有据的观点被看见，而不是被情绪淹没。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <h3 className="heading-serif text-base font-bold text-foreground-950">对社区</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                    保持干净、可读、不被打扰，是长期做下去的前提。
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="principles" className="border-b border-background-200/60 bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Reveal>
            <span className="flex items-center gap-2 text-xs tracking-[0.3em] text-accent-500">
              <i className="ri-book-marked-line" />
              CONTENT PRINCIPLES
            </span>
            <h2 className="heading-serif mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
              四条内容原则
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 50}>
                <div className="flex h-full gap-4 rounded-lg border border-background-200/80 bg-background-100 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-500">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <h3 className="heading-serif text-lg font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-background-200/60 bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Reveal>
            <span className="flex items-center gap-2 text-xs tracking-[0.3em] text-primary-500">
              <i className="ri-route-line" />
              OUR STORY
            </span>
            <h2 className="heading-serif mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
              发展理念
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {timeline.map((item, index) => (
              <Reveal key={item.year} delay={index * 60}>
                <div className="relative h-full rounded-lg border border-background-200/80 bg-background-50 p-5">
                  <span className="brand-font absolute right-4 top-3 text-3xl text-background-300">
                    0{index + 1}
                  </span>
                  <span className="inline-flex rounded-full bg-secondary-100/80 px-2.5 py-1 text-xs text-secondary-900">
                    {item.year}
                  </span>
                  <h3 className="heading-serif mt-3 text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-background-200/60 bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Reveal>
            <span className="flex items-center gap-2 text-xs tracking-[0.3em] text-secondary-500">
              <i className="ri-heart-3-line" />
              COMMUNITY VOICES
            </span>
            <h2 className="heading-serif mt-3 text-2xl font-black text-foreground-950 md:text-3xl">
              用户口碑
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {members.map((member, index) => (
              <Reveal key={member.id} delay={index * 40}>
                <figure className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-100 p-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-sm font-medium text-secondary-900">
                      {member.avatar}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{member.name}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">
                        {member.role} · {member.joined}
                      </p>
                    </div>
                  </div>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-600">
                    {member.quote}
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-4 border-t border-background-200/70 pt-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-heart-3-line text-primary-400" />
                      {member.likes}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-chat-3-line" />
                      {member.comments}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="bg-background-100/60 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Reveal>
            <div className="flex flex-col gap-6 rounded-lg border border-primary-300/40 bg-primary-50/50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
              <div>
                <h2 className="heading-serif text-2xl font-black text-foreground-950">
                  和我们聊聊，或推荐一个话题
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-500">
                  无论是内容建议、合作沟通，还是你想推荐一个值得讨论的话题，都可以直接写邮件给我们。通常会在两个工作日内回复。
                </p>
                <a
                  href="mailto:hello@shenyechigua.com"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-accent-500 transition-colors hover:text-accent-700"
                >
                  <i className="ri-mail-send-line" />
                  hello@shenyechigua.com
                </a>
              </div>
              <Link
                to="/topics"
                className="inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                <i className="ri-chat-new-line" />
                进入话题广场
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}